import { useVirtualizer } from "@tanstack/react-virtual";
import { cn, useDebounceState } from "@theprelude/core";
import {
  Button,
  ButtonProps,
  CloseIcon,
  Dropdown,
  DropdownCheckbox,
  DropdownContent,
  DropdownItem,
  DropdownTrigger,
  IconButton,
  Input,
  RotatingChevron,
  SearchIcon,
  Truncate,
} from "@theprelude/ds";
import { CSSProperties, ReactNode, useRef } from "react";
import { usePopper } from "@/hooks/use-popper";
import { fuzzyFilter } from "@/lib/fuzzy-filters";
import { Highlight } from "./highlight";

type DropdownSelectProps<
  TValue,
  TOption,
  TMultiple extends boolean,
> = ButtonProps & {
  options: TOption[];
  selection: Set<TValue>;
  placeholder?: string;
  getLabel: (option: TOption) => string;
  getValue: (option: TOption) => TValue;
  contentSize?: "small" | "medium";
  align?: "start" | "end";
  searchDelay?: number;
  renderOption?: (opts: { option: TOption; label: ReactNode }) => ReactNode;
} & (TMultiple extends true
    ? { multiple: true; onChangeSelection: (selection: Set<TValue>) => void }
    : { multiple?: false; onChangeSelection: (selection: TValue) => void });

const defaultRenderOption: NonNullable<
  DropdownSelectProps<unknown, unknown, boolean>["renderOption"]
> = ({ label }) => label;

export function DropdownSelect<TValue, TOption, TMultiple extends boolean>(
  props: DropdownSelectProps<TValue, TOption, TMultiple>,
) {
  const {
    options,
    multiple,
    placeholder,
    onChangeSelection,
    getLabel,
    getValue,
    renderOption = defaultRenderOption,
    contentSize = "small",
    align = "end",
    searchDelay = 500,
    ...buttonProps
  } = props;

  const dropdown = usePopper();
  const searchTerm = useDebounceState<string>("", searchDelay);

  const filteredOptions = options.filter((option) =>
    fuzzyFilter(
      getLabel(option),
      searchTerm.debouncedValue.toLocaleLowerCase(),
    ),
  );

  return (
    <Dropdown {...dropdown}>
      <DropdownTrigger asChild>
        <Button
          icon={
            <RotatingChevron
              className="text-secondary-foreground"
              active={dropdown.open}
            />
          }
          iconPosition={buttonProps.icon ? "left" : "right"}
          {...buttonProps}
        />
      </DropdownTrigger>

      <DropdownContent
        align={align}
        className={cn(contentSize === "small" ? "w-64" : "w-96")}
      >
        <Input
          value={searchTerm.value}
          onChange={(ev) => {
            searchTerm.setDelayed(ev.target.value);
          }}
          onKeyDown={(ev) => {
            if (ev.key !== "Escape") {
              ev.stopPropagation();
            }
          }}
          before={<SearchIcon className="text-secondary-foreground" />}
          after={
            <IconButton
              icon={<CloseIcon />}
              onClick={() => {
                searchTerm.setImmediate("");
              }}
            />
          }
          placeholder={placeholder}
          className="rounded-none border border-x-0 border-t-0 border-secondary-background text-xs"
        />
        {multiple && (
          <div className="flex items-center">
            <Button
              intent="text-link"
              onClick={() =>
                onChangeSelection(
                  new Set(filteredOptions.map((option) => getValue(option))),
                )
              }
            >
              Select All
            </Button>
            <Button
              intent="text-link"
              onClick={() => onChangeSelection(new Set<TValue>())}
            >
              Clear
            </Button>
          </div>
        )}
        <VirtualizedOptions
          {...props}
          options={filteredOptions}
          renderOption={renderOption}
          searchTerm={searchTerm.debouncedValue}
        />
      </DropdownContent>
    </Dropdown>
  );
}

type VirtualizedOptionsProps<
  TValue,
  TOption,
  TMultiple extends boolean,
> = DropdownSelectProps<TValue, TOption, TMultiple> & {
  renderOption: NonNullable<
    DropdownSelectProps<TValue, TOption, TMultiple>["renderOption"]
  >;
  searchTerm: string;
  contentSize?: "small" | "medium";
};

function VirtualizedOptions<TValue, TOption, TMultiple extends boolean>({
  multiple,
  options,
  selection,
  searchTerm,
  onChangeSelection,
  getLabel,
  getValue,
  renderOption,
  contentSize,
}: VirtualizedOptionsProps<TValue, TOption, TMultiple>) {
  const parentRef = useRef<HTMLDivElement>(null);
  const rowVirtualizer = useVirtualizer({
    count: options.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 36,
    measureElement:
      typeof window !== "undefined" &&
      navigator.userAgent?.indexOf("Firefox") === -1
        ? (element) => element?.getBoundingClientRect().height
        : undefined,
    overscan: 5,
  });

  return (
    <div ref={parentRef} className="max-h-[300px] min-h-[70px] overflow-y-auto">
      <div
        style={{
          height: `${rowVirtualizer.getTotalSize()}px`,
          width: "100%",
          position: "relative",
        }}
      >
        {rowVirtualizer.getVirtualItems().map((virtualItem) => {
          const option = options[virtualItem.index] as TOption;
          const optionLabel = getLabel(option);
          const optionValue = getValue(option);

          const item = renderOption({
            option,
            label: (
              <div className={cn(contentSize === "small" && "max-w-52")}>
                <Truncate text={optionLabel}>
                  <Highlight text={optionLabel} highlight={searchTerm} />
                </Truncate>
              </div>
            ),
          });

          const containerStyle: CSSProperties = {
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: `${virtualItem.size}px`,
            transform: `translateY(${virtualItem.start}px)`,
          };

          const key = `${optionLabel}-${optionValue}`;

          if (!multiple) {
            return (
              <DropdownItem
                key={key}
                onSelect={() => onChangeSelection(optionValue)}
                style={containerStyle}
              >
                {item}
              </DropdownItem>
            );
          }

          return (
            <DropdownCheckbox
              key={key}
              checked={selection.has(optionValue)}
              onCheckedChange={(value) => {
                const updatedValues = new Set<TValue>(selection);
                if (value) {
                  updatedValues.add(optionValue);
                } else {
                  updatedValues.delete(optionValue);
                }
                onChangeSelection(updatedValues);
              }}
              onSelect={(ev) => ev.preventDefault()}
              style={containerStyle}
            >
              {item}
            </DropdownCheckbox>
          );
        })}
      </div>
    </div>
  );
}
